// src/admin.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, getDocs, doc, getDoc } from 'firebase/firestore';

const WALLET_ID = 'rdX289s3YBP31Hw5qn2eXVywtBq1';
const EXCLUDED_UID = 'BkcvH1wVHTVCxRUCpWkOeWGGodq2';

function Section({ title, children }) {
  return (
    <div className="card">
      <h2 className="card-title">{title}</h2>
      {children}
    </div>
  );
}

export default function Admin() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [earnedCredits, setEarnedCredits] = useState(null);
  const [poolCents, setPoolCents] = useState(null);
  const [excludedUserUsd, setExcludedUserUsd] = useState(null);

  const [loadingBalances, setLoadingBalances] = useState(false);
  const [sumUsdAllUsers, setSumUsdAllUsers] = useState(null);
  const [topTen, setTopTen] = useState([]);

  const [loadingUtility, setLoadingUtility] = useState(false);
  const [sumUtilityUsdAllUsers, setSumUtilityUsdAllUsers] = useState(null);
  const [topTenUtility, setTopTenUtility] = useState([]);

  const [activeTab, setActiveTab] = useState('wallets');
  const [loadingVotes, setLoadingVotes] = useState(false);
  const [votes, setVotes] = useState([]);

  const getAllBalances = useCallback(async () => {
    setLoadingBalances(true);
    setErr(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getAllWalletBalancesV1');
      const res = await fn({ platformWalletId: WALLET_ID, excludeUserId: EXCLUDED_UID });

      const data = res?.data || {};
      setSumUsdAllUsers(
        Number.isFinite(Number(data.totalUsd)) ? Number(data.totalUsd) : null
      );
      setTopTen(Array.isArray(data.top10) ? data.top10 : []);
    } catch (e) {
      setErr(e?.message || 'Error getting balances');
    } finally {
      setLoadingBalances(false);
    }
  }, []);

  const getAllUtilityBalances = useCallback(async () => {
    setLoadingUtility(true);
    setErr(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getAllUtilityBalancesV1');
      const res = await fn({
        platformWalletId: WALLET_ID,
        excludeUserIds: [EXCLUDED_UID, 'h0fEIStkM4XHTDErZM5i8XIo9rA3'],
      });
      const data = res?.data || {};
      setSumUtilityUsdAllUsers(
        Number.isFinite(Number(data.totalUsd)) ? Number(data.totalUsd) : null
      );
      setTopTenUtility(Array.isArray(data.top10) ? data.top10 : []);
    } catch (e) {
      setErr(e?.message || 'Error getting utility balances');
    } finally {
      setLoadingUtility(false);
    }
  }, []);

  const getShowcaseVotes = useCallback(async () => {
    setLoadingVotes(true);
    setErr(null);
    try {
      const db = getFirestore();

      // 1) Fetch all artist vote counts
      const countsSnap = await getDocs(
        collection(db, 'artistShowcase', 'signups', 'artistCounts')
      );

      // 2) Fetch signups doc to get artist metadata (name, username, etc.)
      const signupsDocRef = doc(db, 'artistShowcase', 'signups');
      const signupsDocSnap = await getDoc(signupsDocRef);
      const signupsData = signupsDocSnap.exists() ? signupsDocSnap.data() || {} : {};
      const artistsMap = signupsData.artists || {};

      // 3) Build array of artists with votes + metadata
      const items = countsSnap.docs
        .map((docSnap) => {
          const data = docSnap.data() || {};
          const artistId = data.artistId || docSnap.id;
          const countFromCounts = Number(data.count) || 0;

          const payloadArr = artistsMap[artistId] || [];
          const latestPayload =
            Array.isArray(payloadArr) && payloadArr.length > 0
              ? payloadArr[payloadArr.length - 1]
              : {};

          const name = latestPayload.name || '';
          const username = latestPayload.username || '';
          const legacyVotes = Number(
            latestPayload.votesCount != null ? latestPayload.votesCount : 0
          ) || 0;

          const totalVotes = countFromCounts + legacyVotes;

          return {
            userId: artistId,
            votes: totalVotes,
            name,
            username,
          };
        })
        .sort((a, b) => b.votes - a.votes);


      setVotes(items);
    } catch (e) {
      console.error('Error loading showcase votes:', e);
      setErr(e?.message || 'Error loading votes');
    } finally {
      setLoadingVotes(false);
    }
  }, []);

  const poolUsd = useMemo(() => {
    const n = Number(poolCents);
    if (!Number.isFinite(n)) return null;
    return (n / 100).toFixed(2);
  }, [poolCents]);

  const earnedUsd = useMemo(() => {
    const n = Number(earnedCredits);
    if (!Number.isFinite(n)) return null;
    return (n * 0.1).toFixed(2);
  }, [earnedCredits]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getAdminDashboardV1');
      const [resPlatform, resExcluded] = await Promise.all([
        fn({ walletId: WALLET_ID }),
        fn({ walletId: EXCLUDED_UID }),
      ]);
      const platformData = resPlatform?.data || {};
      const excludedData = resExcluded?.data || {};
      setEarnedCredits(platformData.earnedCreditsTotal ?? null);
      setPoolCents(platformData.poolCents ?? null);
      setExcludedUserUsd(
        Number.isFinite(Number(excludedData.earnedCreditsTotal))
          ? Number(excludedData.earnedCreditsTotal) * 0.1
          : null
      );
    } catch (e) {
      setErr(e?.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="homepage-container">
      <div style={{ padding: '2rem' }}>
        <h1>DAT Chain • Admin</h1>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <button
            className="btn"
            onClick={() => setActiveTab('wallets')}
            disabled={activeTab === 'wallets'}
          >
            Wallets
          </button>
          <button
            className="btn"
            onClick={() => setActiveTab('votes')}
            disabled={activeTab === 'votes'}
          >
            Votes
          </button>
        </div>

        {activeTab === 'wallets' && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 18,
                justifyContent: 'center',
              }}
            >
              <button className="btn" onClick={refresh} disabled={loading}>
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            {err && (
              <div className="alert">
                {err}
              </div>
            )}

            <Section title="SPNDAT Platform Balance">
              <div style={{ opacity: 0.8, fontSize: 12, marginBottom: 8 }} />
              <div style={{ opacity: 0.65, fontSize: 12, marginBottom: 12 }} />
              {earnedUsd == null ? (
                <div style={{ opacity: 0.8 }}>No data found.</div>
              ) : (
                <div className="metric">${earnedUsd}</div>
              )}

              {Number.isFinite(Number(earnedCredits)) && (
                <div
                  style={{ opacity: 0.65, fontSize: 12, marginTop: 6 }}
                />
              )}
            </Section>

            <Section title="Street Flavor Balance">
              <div style={{ opacity: 0.8, fontSize: 12, marginBottom: 8 }} />
              <div style={{ opacity: 0.65, fontSize: 12, marginBottom: 12 }} />
              {excludedUserUsd == null ? (
                <div style={{ opacity: 0.8 }}>No data found.</div>
              ) : (
                <div className="metric">${excludedUserUsd.toFixed(2)}</div>
              )}
            </Section>

            <Section title="Liquidity Pool">
              <div style={{ opacity: 0.65, fontSize: 12, marginBottom: 12 }} />
              {poolUsd == null ? (
                <div style={{ opacity: 0.8 }}>No poolCents found.</div>
              ) : (
                <div className="metric">${poolUsd}</div>
              )}
              {Number.isFinite(Number(poolCents)) && (
                <div
                  style={{ opacity: 0.65, fontSize: 12, marginTop: 6 }}
                />
              )}
            </Section>
          </>
        )}

        {activeTab === 'votes' && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 18,
              }}
            >
              <button
                className="btn"
                onClick={getShowcaseVotes}
                disabled={loadingVotes}
              >
                {loadingVotes ? 'Loading votes…' : 'Load showcase votes'}
              </button>
            </div>

            {err && (
              <div className="alert">
                {err}
              </div>
            )}

            <Section title="Artist Showcase Votes">
              {votes.length === 0 ? (
                <div style={{ opacity: 0.8 }}>
                  No votes found yet. Click &quot;Load showcase votes&quot; above.
                </div>
              ) : (
                <ol style={{ paddingLeft: 18, lineHeight: 1.6 }}>
                  {votes.map((v, index) => (
                    <li key={v.userId}>
                      <span style={{ fontWeight: 700 }}>{index + 1}.</span>{' '}
                      <span style={{ fontWeight: 700 }}>
                        {v.name || v.username || v.userId}
                      </span>
                      {v.username ? ` (@${v.username})` : ''}
                      <div style={{ opacity: 0.8, fontSize: 12 }}>
                        userId: {v.userId} • votes: {v.votes}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Section>
          </>
        )}
      </div>

      <style>{`
        :root{
          --brand-top:#fe24ad;
          --brand-bot:#a410e8;
          --brand-grad:linear-gradient(135deg,var(--brand-top),var(--brand-bot));
          --brand-top-20:rgba(254,36,173,.2);
          --brand-bot-40:rgba(164,16,232,.4);
        }
        *{margin:0;padding:0;box-sizing:border-box;}
        html,body{
          width:100vw;
          min-height:100vh;
          overflow-y:auto;
          overflow-x:hidden;
          background:#000;
          font-family:sans-serif;
          color:#fff;
        }

        ::-webkit-scrollbar{width:8px;}
        ::-webkit-scrollbar-track{background:#050008;}
        ::-webkit-scrollbar-thumb{
          background:var(--brand-grad);
          border-radius:4px;box-shadow:0 0 8px rgba(254,36,173,.3);
        }
        .homepage-container{
          width:100vw;
          min-height:100vh;
          height:auto;
          overflow-y:auto;
          background:
            radial-gradient(800px 600px at 15% 10%, var(--brand-top-20), transparent 60%),
            radial-gradient(700px 500px at 85% 90%, var(--brand-bot-40), transparent 60%),
            #000;
          color:#fff;
          padding:2rem;
          display:flex;
          flex-direction:column;
          animation:fadeInPage 1s ease-in-out;
          border-radius:0;
        }

        @keyframes fadeInPage{from{opacity:0;}to{opacity:1;}}
        h1{
          text-align:center;margin-bottom:1rem;font-size:2.5rem;font-weight:800;
          background:var(--brand-grad);
          -webkit-background-clip:text;background-clip:text;
          -webkit-text-fill-color:transparent;
          text-shadow:0 0 18px rgba(164,16,232,.25);
        }
        .card{
          background:#050008;
          color:#fff;
          border:1px solid var(--brand-bot-40);
          border-radius:14px;
          padding:16px;
          margin-bottom:16px;
          box-shadow:0 6px 18px rgba(0,0,0,0.25);
        }
        .card-title{
          margin:0 0 10px 0;
          font-size:18px;
          font-weight:800;
          background:var(--brand-grad);
          -webkit-background-clip:text;background-clip:text;
          -webkit-text-fill-color:transparent;
        }
        .metrics-row{
          display:grid;
          grid-template-columns:1fr;
          gap:16px;
        }
        @media (min-width: 900px){
          .metrics-row{
            grid-template-columns:1fr 1fr;
          }
        }

        .btn{
          padding:10px 14px;
          border-radius:10px;
          border:1px solid rgba(255,255,255,0.12);
          background:var(--brand-grad);
          color:#fff;
          cursor:pointer;
          font-weight:700;
          letter-spacing:.2px;
          box-shadow:0 4px 12px rgba(0,0,0,.35);
        }
        .btn:disabled{
          opacity。.7;cursor:default;
          background:linear-gradient(135deg, rgba(254,36,173,.5), rgba(164,16,232,.5));
        }
        .metric{
          font-size:28px;
          font-weight:800;
          letter-spacing。.2px;
        }
        .alert{
          background:#3a0b0b;
          color:#ffd7d7;
          border:1px solid #a33;
          border-radius:10px;
          padding:12px;
          margin-bottom:16px;
        }
        @media (max-width:600px){
          .homepage-container{padding:1.5rem 1rem;}
          h1{font-size:2rem;}
        }
      `}</style>
    </div>
  );
}
